// src/components/RouteClosuresList.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { subscribeToRouteClosures, closeDispute } from '../services/routeClosuresService';
import { createCommission } from '../services/commissionsService';
import { authService } from '../services/authService';
import RouteDetailModal from './RouteDetailModal';
import AddCommissionModal from './AddCommissionModal';
import RouteGenerator from './RouteGenerator';
import { useTecnicos } from '../hooks/useTecnicos';

// ── Pure helpers ────────────────────────────────────────────────────────────────
const toDate = (d) => d?.toDate ? d.toDate() : d instanceof Date ? d : d ? new Date(d) : null;

const isSameDay = (a, b) => {
  const da = toDate(a); const db = toDate(b);
  return da && db && da.toDateString() === db.toDateString();
};

const formatDateShort = (d) => {
  const dd = toDate(d);
  if (!dd) return 'Sin fecha';
  return dd.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatTime = (d) => {
  const dd = toDate(d);
  if (!dd) return '';
  return dd.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

const getCompletionPct = (assigned, completed) => {
  const a = Number(assigned) || 0;
  const c = Number(completed) || 0;
  return a > 0 ? Math.round((c / a) * 100) : 0;
};

// `cash` es un booleano (¿hubo algo de efectivo?), no el importe — el
// importe real está en `efectivo` (ver manageRouteClosure.js).
const getEfectivo = (c) => {
  const ef = c.efectivo;
  return ef !== undefined && ef !== null ? Number(ef) : null;
};

const getWeekDays = () => {
  const today = new Date();
  const dow = today.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(today);
  monday.setDate(today.getDate() + offset);
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
};

const exportCSV = (closures, resolveNombre = () => null) => {
  const header = ['Fecha', 'Hora cierre', 'Técnico', 'Asignadas', 'Completadas', '% Completitud', 'Efectivo (€)', 'Notas'];
  const rows = closures.map(c => {
    const d = toDate(c.date);
    const t = toDate(c.createdAt || c.date);
    const ef = getEfectivo(c);
    return [
      d ? d.toLocaleDateString('es-ES') : '',
      t ? t.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '',
      c.employeeName || resolveNombre(c.employeeId) || c.employeeId,
      c.assigned  || 0,
      c.completed || 0,
      `${getCompletionPct(c.assigned, c.completed)}%`,
      ef !== null ? ef.toFixed(2) : '',
      (c.notes || '').replace(/[\n\r]+/g, ' '),
    ];
  });
  const csv = [header, ...rows]
    .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `cierres_rutas_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ── Generación de informes PDF (window.print) ───────────────────────────────────
const openPrintWindow = (html) => {
  const win = window.open('', '_blank', 'width=960,height=720');
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 600);
};

const pctCol = (p) => p >= 90 ? '#059669' : p >= 75 ? '#d97706' : '#dc2626';
// La tasa de cierre solo tiene dos estados a efectos de aviso: por debajo
// del 90% se destaca en rojo (umbral pedido explícitamente), si no verde.
const pctColCierre = (p) => (p ?? 0) >= 90 ? '#059669' : '#dc2626';

const MOTIVO_LABEL = {
  cliente_ausente:     'Cliente ausente',
  incidencia_tecnica:  'Incidencia técnica',
  emergencia_personal: 'Emergencia personal',
  fallo_app:           'Fallo de la app',
  otro:                'Otro',
};

const REPORT_CSS = `
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:Arial,sans-serif;color:#1f2937;padding:32px 40px;font-size:13px}
  .hdr{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;border-bottom:3px solid #dc2626;padding-bottom:16px}
  .hdr h1{font-size:22px;color:#dc2626;font-weight:700;margin-bottom:2px}
  .hdr .tname{font-size:18px;font-weight:700;color:#111827;margin:4px 0}
  .hdr .sub{color:#6b7280;font-size:13px}
  .hdr-r{text-align:right;font-size:11px;color:#9ca3af}
  .hdr-r strong{color:#dc2626}
  .stats{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:28px}
  .stat{border:1px solid #e5e7eb;border-radius:10px;padding:16px;text-align:center}
  .stat .lbl{font-size:10px;text-transform:uppercase;color:#9ca3af;letter-spacing:.05em;margin-bottom:8px}
  .stat .val{font-size:28px;font-weight:700;color:#111827}
  .stat .sub2{font-size:11px;color:#9ca3af;margin-top:4px}
  h2{font-size:14px;color:#374151;margin-bottom:12px;font-weight:600}
  table{width:100%;border-collapse:collapse;margin-bottom:24px}
  th{background:#f9fafb;text-align:left;padding:10px 14px;font-size:11px;text-transform:uppercase;color:#6b7280;letter-spacing:.05em;border-bottom:2px solid #e5e7eb}
  td{padding:11px 14px;border-bottom:1px solid #f3f4f6;font-size:13px;vertical-align:middle}
  tr:last-child td{border-bottom:none}
  .bar-w{background:#f3f4f6;border-radius:4px;height:6px;margin-top:4px;overflow:hidden}
  .bar{height:6px;border-radius:4px}
  .footer{margin-top:36px;padding-top:14px;border-top:1px solid #e5e7eb;text-align:center;font-size:10px;color:#9ca3af}
  @media print{body{padding:20px 28px}@page{margin:1.5cm}}
`;

const genDate = () => new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });

// Informe de rendimiento (pantalla y PDF) — consume la respuesta de
// getInformeRendimiento.js (rutas ESPERADAS vs CERRADAS), no solo los
// cierres que existen. `tecnicoFiltro` es el nombre a mostrar en cabecera
// cuando el informe está limitado a un solo técnico (null = equipo completo).
const informeHTML = (data, periodoLabel, tecnicoFiltro) => {
  const { kpis, porTecnico, porDia } = data;

  const filasTecnico = porTecnico.map(t => `<tr>
      <td>${t.nombre}</td>
      <td style="text-align:center">${t.diasEsperados}</td>
      <td style="text-align:center">${t.diasCerrados}</td>
      <td style="text-align:center">${t.diasNoCerrados}</td>
      <td style="text-align:center"><strong style="color:${pctColCierre(t.pctCierre)}">${t.pctCierre ?? '—'}${t.pctCierre != null ? '%' : ''}</strong></td>
      <td style="text-align:center">${t.pctEnHorario != null ? t.pctEnHorario + '%' : '—'}</td>
      <td style="text-align:center">${t.ordenesAsignadas}</td>
      <td style="text-align:center">${t.ordenesCompletadas}</td>
      <td style="text-align:center">${t.pctCompletitud != null ? t.pctCompletitud + '%' : '—'}</td>
      <td style="text-align:center">${t.diasSinFichaje}</td>
    </tr>`).join('');

  const filasDia = porDia.map(d => {
    const fecha = new Date(d.fecha + 'T12:00:00');
    const faltan = d.rutasCerradas < d.rutasEsperadas;
    return `<tr${faltan ? ' style="background:#fef2f2"' : ''}>
      <td style="text-transform:capitalize">${fecha.toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long' })}</td>
      <td style="text-align:center">${d.rutasEsperadas}</td>
      <td style="text-align:center">${d.rutasCerradas}</td>
      <td style="color:#dc2626;font-size:12px">${d.tecnicosNoCerraron.join(', ') || '—'}</td>
    </tr>`;
  }).join('');

  const titulo = tecnicoFiltro ? `Informe de Rendimiento — ${tecnicoFiltro}` : 'Informe de Rendimiento del Equipo';

  return `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>${titulo}</title>
<style>${REPORT_CSS}</style></head><body>
  <div class="hdr">
    <div><h1>${titulo}</h1><div class="sub">${periodoLabel}</div></div>
    <div class="hdr-r">Generado el ${genDate()}<br><strong>CONFIDENCIAL</strong></div>
  </div>
  <div class="stats">
    <div class="stat"><div class="lbl">Rutas esperadas</div><div class="val">${kpis.rutasEsperadas}</div></div>
    <div class="stat"><div class="lbl">Rutas cerradas</div><div class="val">${kpis.rutasCerradas}</div></div>
    <div class="stat"><div class="lbl">Tasa de cierre</div><div class="val" style="color:${pctColCierre(kpis.tasaCierre)}">${kpis.tasaCierre ?? '—'}${kpis.tasaCierre != null ? '%' : ''}</div></div>
    <div class="stat"><div class="lbl">Rutas no cerradas</div><div class="val" style="color:#dc2626">${kpis.rutasNoCerradas}</div></div>
    <div class="stat"><div class="lbl">Órdenes asignadas</div><div class="val">${kpis.ordenesAsignadas}</div></div>
    <div class="stat"><div class="lbl">Órdenes completadas</div><div class="val">${kpis.ordenesCompletadas}</div></div>
    <div class="stat"><div class="lbl">Completitud</div><div class="val" style="color:${pctCol(kpis.completitud ?? 0)}">${kpis.completitud ?? '—'}${kpis.completitud != null ? '%' : ''}</div></div>
  </div>
  <h2>Por técnico</h2>
  <table>
    <thead><tr>
      <th>Técnico</th><th style="text-align:center">Esperadas</th><th style="text-align:center">Cerradas</th>
      <th style="text-align:center">No cerradas</th><th style="text-align:center">% Cierre</th>
      <th style="text-align:center">% En horario</th><th style="text-align:center">Asignadas</th>
      <th style="text-align:center">Completadas</th><th style="text-align:center">% Completitud</th>
      <th style="text-align:center">Sin fichaje</th>
    </tr></thead>
    <tbody>${filasTecnico || '<tr><td colspan="10" style="text-align:center;color:#9ca3af;padding:20px">Sin datos para este período</td></tr>'}</tbody>
  </table>
  <h2>Día a día</h2>
  <table>
    <thead><tr>
      <th>Día</th><th style="text-align:center">Esperadas</th><th style="text-align:center">Cerradas</th><th>Técnicos sin cerrar</th>
    </tr></thead>
    <tbody>${filasDia || '<tr><td colspan="4" style="text-align:center;color:#9ca3af;padding:20px">Sin días laborables en este período</td></tr>'}</tbody>
  </table>
  <div class="footer">Documento generado automáticamente — Uso interno del equipo</div>
</body></html>`;
};

// ── StatusCell ──────────────────────────────────────────────────────────────────
const StatusCell = ({ closure, size = 'md' }) => {
  const cls = `inline-block font-semibold rounded-md ${size === 'sm' ? 'text-xs px-1.5 py-0.5' : 'text-sm px-2 py-1'}`;
  if (!closure) return <span className={`${cls} bg-gray-100 text-gray-400`}>—</span>;
  const pct = getCompletionPct(closure.assigned, closure.completed);
  if (pct >= 100) return <span className={`${cls} bg-green-100 text-green-700`}>✓ {pct}%</span>;
  if (pct > 0)    return <span className={`${cls} bg-amber-100 text-amber-700`}>⚠ {pct}%</span>;
  return              <span className={`${cls} bg-red-100 text-red-700`}>✗ 0%</span>;
};

// ── Collapsible wrapper ─────────────────────────────────────────────────────────
const Collapsible = ({ title, isOpen, onToggle, badge, children }) => (
  <div className="bg-white rounded-2xl shadow-lg border overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 md:p-5 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-bold text-gray-800">{title}</h3>
        {badge}
      </div>
      <svg className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {isOpen && <div className="px-4 pb-5 md:px-6">{children}</div>}
  </div>
);

// ── Main component ──────────────────────────────────────────────────────────────
const RouteClosuresList = () => {
  const [closures, setClosures]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);

  // Historial filters
  const [filterEmployee, setFilterEmployee]   = useState('all');
  const [timeFilter, setTimeFilter]           = useState('month');
  const [soloIncompletas, setSoloIncompletas] = useState(false);

  // Sections
  const [semanaOpen, setSemanaOpen]           = useState(true);
  const [calendarOpen, setCalendarOpen]       = useState(false);
  const [anomaliasOpen, setAnomaliasOpen]     = useState(false);
  const [notasOpen, setNotasOpen]             = useState(true);
  const [calendarMonth, setCalendarMonth]     = useState(new Date());

  // Modals
  const [selectedClosure, setSelectedClosure] = useState(null);
  const [commissionOpen, setCommissionOpen]   = useState(false);
  const [commissionInit, setCommissionInit]   = useState(null);

  // Informe de rendimiento (Mejora 1) — rutas esperadas vs cerradas,
  // pedido a getInformeRendimiento.js, no derivado de `closures` en memoria.
  const [informeOpen, setInformeOpen]         = useState(true);
  const [informePeriodMode, setInformePeriodMode] = useState('month'); // 'month' | 'range'
  const [informeMonth, setInformeMonth]       = useState(() => new Date().toISOString().slice(0, 7));
  const [informeDesde, setInformeDesde]       = useState(() => new Date().toISOString().slice(0, 8) + '01');
  const [informeHasta, setInformeHasta]       = useState(() => new Date().toISOString().split('T')[0]);
  const [informeTecnico, setInformeTecnico]   = useState('all');
  const [informeData, setInformeData]         = useState(null);
  const [informeLoading, setInformeLoading]   = useState(false);
  const [informeError, setInformeError]       = useState(null);

  // Interruptor "Exigir cierre de la ruta anterior" (Mejora 3, encendido por defecto desde 2026-10-26)
  const [exigirCierre, setExigirCierre]       = useState(true);
  const [exigirCierreLoaded, setExigirCierreLoaded] = useState(false);
  const [savingExigirCierre, setSavingExigirCierre]  = useState(false);

  // Alert hour (persisted in localStorage)
  const [alertHora, setAlertHora] = useState(
    () => parseInt(localStorage.getItem('routeAlertHora') || '18')
  );
  const [editingAlertHora, setEditingAlertHora] = useState(false);

  const { tecnicos } = useTecnicos();
  const getTecnicoNombre = useCallback(
    (id) => tecnicos.find(t => String(t.employeeId) === String(id) || t.id === String(id))?.nombre || null,
    [tecnicos],
  );

  // Notify unclosed technicians
  const [notifying, setNotifying] = useState(false);
  const [notified, setNotified]   = useState(false);

  const handleNotifyUnclosed = async () => {
    if (notifying || alertas.length === 0) return;
    setNotifying(true);
    try {
      await fetch('/.netlify/functions/notifyEmployees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ employeeIds: alertas.map(t => t.employeeId ?? t.id) }),
      });
      setNotified(true);
      setTimeout(() => setNotified(false), 4000);
    } catch { /* silent */ } finally {
      setNotifying(false);
    }
  };

  // ── Real-time subscription ─────────────────────────────────────────────────
  useEffect(() => {
    const unsub = subscribeToRouteClosures(
      (data) => { setClosures(data); setLoading(false); setError(null); },
      ()     => { setError('Error al cargar los cierres de ruta'); setLoading(false); }
    );
    return unsub;
  }, []);

  // ── Config operaciones: "Exigir cierre de la ruta anterior" ────────────────
  useEffect(() => {
    getDoc(doc(db, 'config', 'operaciones'))
      // Activo salvo que se haya apagado explícitamente (false) — mismo
      // criterio que TechnicianOrdersView.js desde 2026-10-26.
      .then(snap => setExigirCierre(!snap.exists() || snap.data().exigirCierreRutaAnterior !== false))
      .catch(() => {})
      .finally(() => setExigirCierreLoaded(true));
  }, []);

  const handleToggleExigirCierre = async () => {
    const next = !exigirCierre;
    setSavingExigirCierre(true);
    try {
      const resp = await fetch('/.netlify/functions/saveConfigOperaciones', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ exigirCierreRutaAnterior: next }),
      });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error || 'Error desconocido');
      setExigirCierre(next);
    } catch (e) {
      alert('No se pudo guardar: ' + e.message);
    } finally {
      setSavingExigirCierre(false);
    }
  };

  // ── Informe de rendimiento: desde/hasta según el modo de período ───────────
  const informeRango = useMemo(() => {
    if (informePeriodMode === 'range') return { desde: informeDesde, hasta: informeHasta };
    const [y, m] = informeMonth.split('-').map(Number);
    const desde = `${informeMonth}-01`;
    const ultimoDia = new Date(y, m, 0).getDate();
    const hasta = `${informeMonth}-${String(ultimoDia).padStart(2, '0')}`;
    return { desde, hasta };
  }, [informePeriodMode, informeMonth, informeDesde, informeHasta]);

  const informePeriodoLabel = useMemo(() => {
    if (informePeriodMode === 'month') {
      const [y, m] = informeMonth.split('-').map(Number);
      return `Mes de ${new Date(y, m - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}`;
    }
    return `Del ${formatDateShort(informeRango.desde)} al ${formatDateShort(informeRango.hasta)}`;
  }, [informePeriodMode, informeMonth, informeRango]);

  const fetchInforme = useCallback(async () => {
    const { desde, hasta } = informeRango;
    if (!desde || !hasta || desde > hasta) { setInformeError('Rango de fechas inválido'); return; }
    setInformeLoading(true); setInformeError(null);
    try {
      const qs = new URLSearchParams({ desde, hasta });
      if (informeTecnico !== 'all') qs.set('tecnicoId', informeTecnico);
      const resp = await fetch(`/.netlify/functions/getInformeRendimiento?${qs}`, {
        headers: authService.authHeader(),
      });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error || 'Error desconocido');
      setInformeData(d);
    } catch (e) {
      setInformeError(e.message);
      setInformeData(null);
    } finally {
      setInformeLoading(false);
    }
  }, [informeRango, informeTecnico]);

  useEffect(() => { if (informeOpen) fetchInforme(); }, [informeOpen, fetchInforme]);

  const handleGenerateInformePDF = () => {
    if (!informeData) return;
    const tecnicoFiltro = informeTecnico !== 'all'
      ? tecnicos.find(t => String(t.id) === String(informeTecnico))?.nombre
      : null;
    openPrintWindow(informeHTML(informeData, informePeriodoLabel, tecnicoFiltro));
  };

  const weekDays = useMemo(getWeekDays, []);

  // ── Panel del día ──────────────────────────────────────────────────────────
  const panelHoy = useMemo(() => {
    const today = new Date();
    return tecnicos.map(tec => ({
      tec,
      closure: closures.find(c => String(c.employeeId) === String(tec.id) && isSameDay(c.date, today)),
    }));
  }, [closures, tecnicos]);

  // ── Alertas ────────────────────────────────────────────────────────────────
  const alertas = useMemo(() => {
    if (new Date().getHours() < alertHora) return [];
    return panelHoy.filter(({ closure }) => !closure).map(({ tec }) => tec);
  }, [panelHoy, alertHora]);

  // ── Timeline del día ───────────────────────────────────────────────────────
  const timelineHoy = useMemo(() =>
    panelHoy
      .filter(x => x.closure)
      .sort((a, b) => {
        const ta = toDate(a.closure.createdAt || a.closure.date) || new Date(0);
        const tb = toDate(b.closure.createdAt || b.closure.date) || new Date(0);
        return ta - tb;
      }),
  [panelHoy]);

  // ── Grid semanal ───────────────────────────────────────────────────────────
  const gridSemana = useMemo(() =>
    weekDays.map(dia => ({
      dia,
      tecnicos: tecnicos.map(tec => ({
        tec,
        closure: closures.find(c => String(c.employeeId) === String(tec.id) && isSameDay(c.date, dia)),
      })),
    })),
  [closures, weekDays, tecnicos]);

  // ── Calendario mensual ─────────────────────────────────────────────────────
  const calendarData = useMemo(() => {
    const year  = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const last  = new Date(year, month + 1, 0).getDate();
    const days  = [];
    for (let d = 1; d <= last; d++) {
      const date = new Date(year, month, d);
      if (date.getDay() === 0 || date.getDay() === 6) continue;
      days.push({
        date,
        isToday: isSameDay(date, new Date()),
        isFuture: date > new Date(),
        tecnicos: tecnicos.map(tec => ({
          tec,
          closure: closures.find(c => String(c.employeeId) === String(tec.id) && isSameDay(c.date, date)),
        })),
      });
    }
    return days;
  }, [closures, calendarMonth, tecnicos]);

  // ── Panel efectivo ─────────────────────────────────────────────────────────
  const panelEfectivo = useMemo(() => {
    const today = new Date();
    const startWeek = (() => {
      const d = new Date(today);
      const dow = d.getDay();
      d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
      d.setHours(0, 0, 0, 0);
      return d;
    })();
    const startMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    let hoy = 0, semana = 0, mes = 0;
    const porTec = {};
    tecnicos.forEach(t => { porTec[t.id] = { nombre: t.nombre, hoy: 0, semana: 0, mes: 0 }; });

    closures.forEach(c => {
      const ef = getEfectivo(c);
      if (ef === null) return;
      const d = toDate(c.date);
      if (!d) return;
      if (isSameDay(d, today)) { hoy   += ef; if (porTec[c.employeeId]) porTec[c.employeeId].hoy   += ef; }
      if (d >= startWeek)      { semana += ef; if (porTec[c.employeeId]) porTec[c.employeeId].semana += ef; }
      if (d >= startMonth)     { mes    += ef; if (porTec[c.employeeId]) porTec[c.employeeId].mes    += ef; }
    });

    return { hoy, semana, mes, porTec: Object.values(porTec) };
  }, [closures, tecnicos]);

  // ── Anomalías ──────────────────────────────────────────────────────────────
  const anomalias = useMemo(() => {
    const result = [];
    closures.forEach(c => {
      const pct  = getCompletionPct(c.assigned, c.completed);
      const ef   = getEfectivo(c);
      const t    = toDate(c.createdAt || c.date);
      const hour = t ? t.getHours() : null;
      const issues = [];

      if (pct < 50 && (!c.notes || !c.notes.trim())) {
        issues.push('Completitud <50% sin nota del director');
      }
      if (hour !== null && (hour < 11 || hour >= 20)) {
        issues.push(`Hora de cierre inusual (${formatTime(t)})`);
      }
      // Avisos de validateOrders() al cerrar ruta (órdenes cobradas sin DNI/
      // cliente StelOrder/importe, órdenes pendientes sin atender...) — lo que
      // hace falta resolver antes de que estas órdenes se puedan facturar bien.
      if (Array.isArray(c.warnings) && c.warnings.length > 0) {
        issues.push(`${c.warnings.length} aviso(s) de facturación/material pendientes de revisar`);
      }

      if (issues.length > 0) result.push({ closure: c, issues });
    });
    return result.sort((a, b) => (toDate(b.closure.date) || 0) - (toDate(a.closure.date) || 0));
  }, [closures]);

  // ── Disputas abiertas ──────────────────────────────────────────────────────
  const disputasAbiertas = useMemo(() =>
    closures
      .filter(c => c.disputeStatus === 'open')
      .sort((a, b) => (toDate(b.date) || 0) - (toDate(a.date) || 0)),
  [closures]);

  // ── Técnico con más órdenes completadas este mes ───────────────────────────
  const tecMasIncidencias = useMemo(() => {
    const startMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const byTec = {};
    tecnicos.forEach(t => { byTec[t.id] = { tec: t, completed: 0 }; });
    closures
      .filter(c => { const d = toDate(c.date); return d && d >= startMonth; })
      .forEach(c => { if (byTec[c.employeeId]) byTec[c.employeeId].completed += Number(c.completed) || 0; });
    return Object.values(byTec).sort((a, b) => b.completed - a.completed)[0] || null;
  }, [closures, tecnicos]);

  // ── Historial filtrado ─────────────────────────────────────────────────────
  const filteredClosures = useMemo(() => {
    const now = new Date(); now.setHours(23, 59, 59, 999);
    let rangeStart = null;
    if (timeFilter !== 'all') {
      rangeStart = new Date();
      if (timeFilter === 'today')   rangeStart.setHours(0, 0, 0, 0);
      if (timeFilter === 'week')    { rangeStart.setDate(rangeStart.getDate() - 7);      rangeStart.setHours(0,0,0,0); }
      if (timeFilter === 'month')   { rangeStart.setMonth(rangeStart.getMonth() - 1);    rangeStart.setHours(0,0,0,0); }
      if (timeFilter === 'quarter') { rangeStart.setMonth(rangeStart.getMonth() - 3);    rangeStart.setHours(0,0,0,0); }
      if (timeFilter === 'year')    { rangeStart.setFullYear(rangeStart.getFullYear()-1); rangeStart.setHours(0,0,0,0); }
    }
    return closures.filter(c => {
      if (filterEmployee !== 'all' && String(c.employeeId) !== String(filterEmployee)) return false;
      if (rangeStart) {
        const d = toDate(c.date);
        if (!d || d < rangeStart || d > now) return false;
      }
      if (soloIncompletas) {
        const a = Number(c.assigned) || 0;
        if (a === 0 || (Number(c.completed) || 0) >= a) return false;
      }
      return true;
    });
  }, [closures, filterEmployee, timeFilter, soloIncompletas]);

  // ── Stats del historial ────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const totalAssigned  = filteredClosures.reduce((s, c) => s + (Number(c.assigned)  || 0), 0);
    const totalCompleted = filteredClosures.reduce((s, c) => s + (Number(c.completed) || 0), 0);
    const totalEfectivo  = filteredClosures.reduce((s, c) => s + (getEfectivo(c) ?? 0), 0);
    const completionRate = totalAssigned > 0 ? (totalCompleted / totalAssigned * 100).toFixed(1) : '0.0';

    const byTec = {};
    filteredClosures.forEach(c => {
      const name = c.employeeName || getTecnicoNombre(c.employeeId) || String(c.employeeId);
      if (!byTec[name]) byTec[name] = { count: 0, assigned: 0, completed: 0 };
      byTec[name].count++;
      byTec[name].assigned  += Number(c.assigned)  || 0;
      byTec[name].completed += Number(c.completed) || 0;
    });
    const perTecnico = Object.entries(byTec)
      .map(([name, t]) => ({
        name, count: t.count,
        completionRate: t.assigned > 0 ? (t.completed / t.assigned * 100).toFixed(1) : '0.0',
      }))
      .sort((a, b) => b.completionRate - a.completionRate);

    return { totalClosures: filteredClosures.length, totalAssigned, totalCompleted, completionRate, totalEfectivo, perTecnico };
  }, [filteredClosures, getTecnicoNombre]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAlertHora = (val) => {
    const v = Math.max(0, Math.min(23, parseInt(val) || 18));
    setAlertHora(v);
    localStorage.setItem('routeAlertHora', v);
    setEditingAlertHora(false);
  };


  const openCommissionModal = () => {
    if (!tecMasIncidencias) return;
    setCommissionInit({ employeeId: String(tecMasIncidencias.tec.id), tipo: 'MAS_INCIDENCIAS' });
    setCommissionOpen(true);
  };

  // ── Loading / Error ────────────────────────────────────────────────────────
  if (loading) return (
    <div className="p-4 md:p-6">
      <div className="bg-white rounded-2xl shadow-lg border p-6 flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
        <span className="ml-3 text-gray-600">Cargando cierres de ruta...</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="p-4 md:p-6">
      <div className="bg-white rounded-2xl shadow-lg border p-6 text-center py-16 text-red-500">
        <p>{error}</p>
      </div>
    </div>
  );

  const today = new Date();

  return (
    <div className="space-y-4 md:space-y-6 p-4 md:p-6">

      {/* ── GENERADOR DE RUTAS ────────────────────────────────────────────── */}
      <RouteGenerator />

      {/* ── ALERTAS ───────────────────────────────────────────────────────── */}
      {alertas.length > 0 && (
        <div className="bg-red-50 border-2 border-red-400 rounded-2xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">⚠️</span>
                <h3 className="font-bold text-red-800">
                  {alertas.length === 1
                    ? `${alertas[0].nombre} aún no ha cerrado la ruta`
                    : `${alertas.length} técnicos sin cerrar ruta`}
                </h3>
              </div>
              <p className="text-sm text-red-700 mb-2">
                Son las {today.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} — hora de alerta: {alertHora}:00
              </p>
              <div className="flex flex-wrap gap-2">
                {alertas.map(tec => (
                  <span key={tec.id} className="px-2 py-1 bg-red-200 text-red-900 rounded-full text-xs font-semibold">
                    {tec.nombre}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleNotifyUnclosed}
                disabled={notifying || notified}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  notified
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white'
                }`}
              >
                {notified ? (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Enviado
                  </>
                ) : notifying ? (
                  'Enviando...'
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    Notificar cierre
                  </>
                )}
              </button>
              <button
                onClick={() => setEditingAlertHora(v => !v)}
                title="Configurar hora de alerta"
                className="text-red-400 hover:text-red-600 p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
          </div>
          {editingAlertHora && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm text-red-700">Hora de alerta:</span>
              <input type="number" min="0" max="23" defaultValue={alertHora}
                className="w-16 px-2 py-1 border border-red-300 rounded text-sm text-center"
                onBlur={e => handleAlertHora(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAlertHora(e.target.value)}
              />
              <span className="text-sm text-red-700">:00</span>
            </div>
          )}
        </div>
      )}

      {/* ── PANEL DEL DÍA ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-lg border p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">
            Estado del día —{' '}
            {today.toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long' })}
          </h3>
          <button
            onClick={() => setEditingAlertHora(v => !v)}
            title="Configurar hora de alerta"
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>

        {editingAlertHora && (
          <div className="mb-4 flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Alertar si no cierran antes de las</span>
            <input type="number" min="0" max="23" defaultValue={alertHora}
              className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm"
              onBlur={e => handleAlertHora(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAlertHora(e.target.value)}
            />
            <span>:00</span>
          </div>
        )}

        {/* Cards de técnicos */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {panelHoy.map(({ tec, closure }) => {
            const pct       = closure ? getCompletionPct(closure.assigned, closure.completed) : null;
            const hasCierre = !!closure;
            const isOk      = pct !== null && pct >= 100;
            const isPartial = pct !== null && pct > 0 && pct < 100;
            const ef        = closure ? getEfectivo(closure) : null;

            return (
              <div
                key={tec.id}
                onClick={() => closure && setSelectedClosure(closure)}
                className={`rounded-xl border-2 p-4 transition-all ${hasCierre ? 'cursor-pointer hover:shadow-md' : ''} ${
                  isOk ? 'border-green-300 bg-green-50' :
                  isPartial ? 'border-amber-300 bg-amber-50' :
                  hasCierre ? 'border-red-300 bg-red-50' :
                  'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="font-bold text-gray-800 text-sm">{tec.nombre}</span>
                  <span className="text-lg">
                    {isOk ? '✅' : isPartial ? '⚠️' : hasCierre ? '❌' : '⏳'}
                  </span>
                </div>
                {hasCierre ? (
                  <div className="space-y-1">
                    <div className={`text-lg font-bold ${isOk ? 'text-green-700' : isPartial ? 'text-amber-700' : 'text-red-700'}`}>
                      {pct}%
                    </div>
                    <div className="text-xs text-gray-600">{closure.completed}/{closure.assigned} órdenes</div>
                    {toDate(closure.createdAt || closure.date) && (
                      <div className="text-xs text-gray-400">{formatTime(closure.createdAt || closure.date)}</div>
                    )}
                    {ef !== null && <div className="text-xs font-semibold text-green-700">€{ef.toFixed(2)}</div>}
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 mt-1">Sin cierre registrado</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Línea de tiempo del día */}
        {timelineHoy.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-500 mb-3">Línea de tiempo de hoy</h4>
            <div className="flex flex-col">
              {timelineHoy.map(({ tec, closure }, idx) => {
                const t   = toDate(closure.createdAt || closure.date);
                const pct = getCompletionPct(closure.assigned, closure.completed);
                const dotColor = pct >= 100 ? 'bg-green-500' : pct > 0 ? 'bg-amber-500' : 'bg-red-500';
                return (
                  <div key={tec.id} className="flex items-center gap-3">
                    <div className="text-xs text-gray-400 w-12 text-right flex-shrink-0">
                      {t ? formatTime(t) : '—'}
                    </div>
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className={`w-3 h-3 rounded-full ${dotColor}`} />
                      {idx < timelineHoy.length - 1 && <div className="w-0.5 h-7 bg-gray-200 my-0.5" />}
                    </div>
                    <div
                      className="flex-1 cursor-pointer pb-4 last:pb-0"
                      onClick={() => setSelectedClosure(closure)}
                    >
                      <span className="font-semibold text-sm text-gray-800">{tec.nombre}</span>
                      <span className="text-xs text-gray-400 ml-2">{pct}% · {closure.completed}/{closure.assigned} órdenes</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── INFORME DE RENDIMIENTO ────────────────────────────────────────── */}
      <div id="informe-rendimiento">
        <Collapsible
          title="Informe de rendimiento"
          isOpen={informeOpen}
          onToggle={() => setInformeOpen(v => !v)}
          badge={informeData && (
            <span className={`px-2 py-0.5 rounded-full text-sm font-bold ${
              (informeData.kpis.tasaCierre ?? 100) >= 90 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {informeData.kpis.tasaCierre ?? '—'}%
            </span>
          )}
        >
          {/* Interruptor de bloqueo (Mejora 3) — encendido por defecto; apagarlo es solo para emergencias */}
          <div className="mb-5 flex items-center justify-between gap-3 bg-gray-50 border rounded-xl p-3">
            <div>
              <p className="text-sm font-semibold text-gray-700">Exigir cierre de la ruta anterior</p>
              <p className="text-xs text-gray-500 mt-0.5">
                La app de técnicos bloquea la pantalla de Órdenes hasta cerrar o justificar una ruta anterior que quedó sin cerrar. El fichaje nunca se bloquea. Apágalo solo si el bloqueo está fallando.
              </p>
            </div>
            <button
              onClick={handleToggleExigirCierre}
              disabled={!exigirCierreLoaded || savingExigirCierre}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${exigirCierre ? 'bg-red-600' : 'bg-gray-300'} disabled:opacity-50`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${exigirCierre ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Filtros */}
          <div className="flex flex-wrap items-end gap-3 mb-5">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Período</label>
              <div className="flex gap-1">
                {[['month', 'Mes'], ['range', 'Rango personalizado']].map(([v, l]) => (
                  <button key={v} onClick={() => setInformePeriodMode(v)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      informePeriodMode === v ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                    }`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {informePeriodMode === 'month' ? (
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Mes</label>
                <input type="month" value={informeMonth} onChange={e => setInformeMonth(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Desde</label>
                  <input type="date" value={informeDesde} onChange={e => setInformeDesde(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Hasta</label>
                  <input type="date" value={informeHasta} onChange={e => setInformeHasta(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </>
            )}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Técnico</label>
              <select value={informeTecnico} onChange={e => setInformeTecnico(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                <option value="all">Todos</option>
                {tecnicos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
              </select>
            </div>
            <button onClick={fetchInforme} disabled={informeLoading}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-semibold hover:bg-gray-900 disabled:opacity-50">
              {informeLoading ? 'Consultando…' : 'Consultar'}
            </button>
            {informeData && (
              <button onClick={handleGenerateInformePDF}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-semibold">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Generar PDF
              </button>
            )}
          </div>

          {informeError && <p className="text-sm text-red-600 mb-3">{informeError}</p>}

          {informeLoading && !informeData && (
            <div className="py-10 text-center text-gray-400 text-sm">Cargando informe…</div>
          )}

          {informeData && (
            <>
              {/* KPIs de cabecera */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <div className="bg-gray-50 border rounded-xl p-3">
                  <div className="text-xs font-semibold text-gray-500 mb-1">Rutas esperadas</div>
                  <div className="text-2xl font-bold text-gray-800">{informeData.kpis.rutasEsperadas}</div>
                </div>
                <div className="bg-gray-50 border rounded-xl p-3">
                  <div className="text-xs font-semibold text-gray-500 mb-1">Rutas cerradas</div>
                  <div className="text-2xl font-bold text-gray-800">{informeData.kpis.rutasCerradas}</div>
                </div>
                <div className={`border rounded-xl p-3 ${(informeData.kpis.tasaCierre ?? 100) >= 90 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className={`text-xs font-semibold mb-1 ${(informeData.kpis.tasaCierre ?? 100) >= 90 ? 'text-green-700' : 'text-red-700'}`}>Tasa de cierre</div>
                  <div className={`text-2xl font-bold ${(informeData.kpis.tasaCierre ?? 100) >= 90 ? 'text-green-800' : 'text-red-800'}`}>
                    {informeData.kpis.tasaCierre ?? '—'}{informeData.kpis.tasaCierre != null ? '%' : ''}
                  </div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <div className="text-xs font-semibold text-red-700 mb-1">No cerradas</div>
                  <div className="text-2xl font-bold text-red-800">{informeData.kpis.rutasNoCerradas}</div>
                </div>
                <div className="bg-gray-50 border rounded-xl p-3">
                  <div className="text-xs font-semibold text-gray-500 mb-1">Órdenes asignadas</div>
                  <div className="text-2xl font-bold text-gray-800">{informeData.kpis.ordenesAsignadas}</div>
                </div>
                <div className="bg-gray-50 border rounded-xl p-3">
                  <div className="text-xs font-semibold text-gray-500 mb-1">Órdenes completadas</div>
                  <div className="text-2xl font-bold text-gray-800">{informeData.kpis.ordenesCompletadas}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                  <div className="text-xs font-semibold text-emerald-700 mb-1">Completitud</div>
                  <div className="text-2xl font-bold text-emerald-800">
                    {informeData.kpis.completitud ?? '—'}{informeData.kpis.completitud != null ? '%' : ''}
                  </div>
                </div>
              </div>

              {/* Por técnico */}
              <h4 className="text-sm font-semibold text-gray-500 mb-2">Por técnico</h4>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left p-2 font-semibold text-gray-700">Técnico</th>
                      <th className="text-center p-2 font-semibold text-gray-700">Esperadas</th>
                      <th className="text-center p-2 font-semibold text-gray-700">Cerradas</th>
                      <th className="text-center p-2 font-semibold text-gray-700">No cerradas</th>
                      <th className="text-center p-2 font-semibold text-gray-700">% Cierre</th>
                      <th className="text-center p-2 font-semibold text-gray-700">% En horario</th>
                      <th className="text-center p-2 font-semibold text-gray-700">Asignadas</th>
                      <th className="text-center p-2 font-semibold text-gray-700">Completadas</th>
                      <th className="text-center p-2 font-semibold text-gray-700">% Completitud</th>
                      <th className="text-center p-2 font-semibold text-gray-700">Sin fichaje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {informeData.porTecnico.length === 0 ? (
                      <tr><td colSpan={10} className="p-4 text-center text-gray-400">Sin datos para este período</td></tr>
                    ) : informeData.porTecnico.map(t => (
                      <tr key={t.tecnicoId} className={`border-b ${t.pctCierre != null && t.pctCierre < 90 ? 'bg-red-50' : ''}`}>
                        <td className="p-2 font-medium text-gray-800">{t.nombre}</td>
                        <td className="p-2 text-center">{t.diasEsperados}</td>
                        <td className="p-2 text-center">{t.diasCerrados}</td>
                        <td className="p-2 text-center">{t.diasNoCerrados}</td>
                        <td className="p-2 text-center">
                          <span className={`font-bold ${t.pctCierre != null && t.pctCierre < 90 ? 'text-red-700' : 'text-green-700'}`}>
                            {t.pctCierre ?? '—'}{t.pctCierre != null ? '%' : ''}
                          </span>
                        </td>
                        <td className="p-2 text-center">{t.pctEnHorario != null ? `${t.pctEnHorario}%` : '—'}</td>
                        <td className="p-2 text-center">{t.ordenesAsignadas}</td>
                        <td className="p-2 text-center">{t.ordenesCompletadas}</td>
                        <td className="p-2 text-center">{t.pctCompletitud != null ? `${t.pctCompletitud}%` : '—'}</td>
                        <td className="p-2 text-center">{t.diasSinFichaje}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Día a día — incluye los días laborables SIN ningún cierre */}
              <h4 className="text-sm font-semibold text-gray-500 mb-2">Día a día</h4>
              <div className="overflow-x-auto max-h-96 overflow-y-auto mb-6 border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="border-b">
                      <th className="text-left p-2 font-semibold text-gray-700">Día</th>
                      <th className="text-center p-2 font-semibold text-gray-700">Esperadas</th>
                      <th className="text-center p-2 font-semibold text-gray-700">Cerradas</th>
                      <th className="text-left p-2 font-semibold text-gray-700">Técnicos sin cerrar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {informeData.porDia.length === 0 ? (
                      <tr><td colSpan={4} className="p-4 text-center text-gray-400">Sin días laborables en este período</td></tr>
                    ) : informeData.porDia.map(d => {
                      const faltan = d.rutasCerradas < d.rutasEsperadas;
                      const fecha = new Date(d.fecha + 'T12:00:00');
                      return (
                        <tr key={d.fecha} className={`border-b ${faltan ? 'bg-red-50' : ''}`}>
                          <td className="p-2 text-gray-700 capitalize">
                            {fecha.toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                          </td>
                          <td className="p-2 text-center">{d.rutasEsperadas}</td>
                          <td className="p-2 text-center">{d.rutasCerradas}</td>
                          <td className="p-2 text-red-700 text-xs">{d.tecnicosNoCerraron.join(', ') || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Justificaciones registradas por los técnicos */}
              {informeData.justificaciones?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 mb-2">Justificaciones registradas</h4>
                  <div className="space-y-2">
                    {informeData.justificaciones.map(j => (
                      <div key={j.id} className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-gray-800">{j.nombre}</span>
                          <span className="text-xs text-gray-400">{formatDateShort(j.fecha)}</span>
                        </div>
                        <div className="text-xs text-amber-800 font-semibold mb-0.5">{MOTIVO_LABEL[j.motivo] || j.motivo}</div>
                        <p className="text-gray-600 text-xs">{j.detalle}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Collapsible>
      </div>

      {/* ── PANEL EFECTIVO + SUGERENCIA COMISIÓN ──────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Efectivo */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg border p-4 md:p-5">
          <h3 className="text-base font-bold text-gray-800 mb-3">Efectivo cobrado</h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[
              { label: 'Hoy',         valor: panelEfectivo.hoy },
              { label: 'Esta semana', valor: panelEfectivo.semana },
              { label: 'Este mes',    valor: panelEfectivo.mes },
            ].map(({ label, valor }) => (
              <div key={label} className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                <div className="text-xs font-semibold text-emerald-700 mb-1">{label}</div>
                <div className="text-xl font-bold text-emerald-800">€{valor.toFixed(2)}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2 pt-1 border-t border-gray-100">
            {panelEfectivo.porTec.map(tec => (
              <div key={tec.nombre} className="flex items-center justify-between text-xs text-gray-500">
                <span className="font-semibold text-gray-700 w-20">{tec.nombre}</span>
                <div className="flex gap-3">
                  <span>Hoy: <strong className="text-gray-800">€{tec.hoy.toFixed(2)}</strong></span>
                  <span>Semana: <strong className="text-gray-800">€{tec.semana.toFixed(2)}</strong></span>
                  <span>Mes: <strong className="text-gray-800">€{tec.mes.toFixed(2)}</strong></span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sugerencia comisión */}
        <div className="bg-white rounded-2xl shadow-lg border p-4 md:p-5 flex flex-col">
          <h3 className="text-base font-bold text-gray-800 mb-0.5">Más incidencias del mes</h3>
          <p className="text-xs text-gray-400 mb-4 capitalize">
            {today.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
          </p>
          {tecMasIncidencias && tecMasIncidencias.completed > 0 ? (
            <>
              <div className="text-2xl font-bold text-gray-800 mb-0.5">{tecMasIncidencias.tec.nombre}</div>
              <div className="text-sm text-gray-500 mb-4 flex-1">
                {tecMasIncidencias.completed} órdenes completadas
              </div>
              <button
                onClick={openCommissionModal}
                className="w-full py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors"
              >
                Crear comisión — €25.00
              </button>
            </>
          ) : (
            <div className="text-sm text-gray-400 flex-1">Sin datos este mes</div>
          )}
        </div>
      </div>

      {/* ── VISTA SEMANAL ─────────────────────────────────────────────────── */}
      <Collapsible title="Vista semanal" isOpen={semanaOpen} onToggle={() => setSemanaOpen(v => !v)}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm">
            <thead>
              <tr>
                <th className="text-left p-2 font-semibold text-gray-600 w-24">Técnico</th>
                {gridSemana.map(({ dia }) => {
                  const isT = isSameDay(dia, new Date());
                  return (
                    <th key={dia.toDateString()} className={`text-center p-2 font-semibold ${isT ? 'text-red-700 bg-red-50' : 'text-gray-600'}`}>
                      <div className="text-xs uppercase">{dia.toLocaleDateString('es-ES', { weekday: 'short' })}</div>
                      <div>{dia.getDate()}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {tecnicos.map(tec => (
                <tr key={tec.id} className="border-t">
                  <td className="p-2 font-semibold text-gray-700">{tec.nombre}</td>
                  {gridSemana.map(({ dia, tecnicos }) => {
                    const { closure } = tecnicos.find(t => t.tec.id === tec.id) || {};
                    const isT = isSameDay(dia, new Date());
                    return (
                      <td key={dia.toDateString()} className={`text-center p-2 ${isT ? 'bg-red-50' : ''}`}>
                        <span onClick={() => closure && setSelectedClosure(closure)} className={closure ? 'cursor-pointer' : ''}>
                          <StatusCell closure={closure} size="sm" />
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => { setInformeOpen(true); document.getElementById('informe-rendimiento')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-semibold"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Ver informe de rendimiento
          </button>
        </div>
      </Collapsible>

      {/* ── CALENDARIO MENSUAL ────────────────────────────────────────────── */}
      <Collapsible title="Calendario mensual" isOpen={calendarOpen} onToggle={() => setCalendarOpen(v => !v)}>
        {/* Navegación de mes */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setCalendarMonth(m => { const d = new Date(m); d.setMonth(d.getMonth() - 1); return d; })}
            className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >←</button>
          <span className="font-semibold text-gray-800 capitalize">
            {calendarMonth.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
          </span>
          <button
            onClick={() => setCalendarMonth(m => { const d = new Date(m); d.setMonth(d.getMonth() + 1); return d; })}
            className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >→</button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>
                <th className="text-left p-2 font-semibold text-gray-600 bg-gray-50 border border-gray-200 w-20">Técnico</th>
                {calendarData.map(({ date, isToday }) => (
                  <th key={date.toDateString()}
                    className={`p-1.5 text-center border border-gray-200 min-w-[36px] font-semibold ${isToday ? 'bg-red-100 text-red-700' : 'bg-gray-50 text-gray-500'}`}>
                    <div className="leading-none">{date.toLocaleDateString('es-ES', { weekday: 'narrow' })}</div>
                    <div className="font-bold mt-0.5">{date.getDate()}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tecnicos.map(tec => (
                <tr key={tec.id}>
                  <td className="p-2 font-semibold text-gray-700 border border-gray-200 bg-gray-50">{tec.nombre}</td>
                  {calendarData.map(({ date, isToday, isFuture, tecnicos }) => {
                    const { closure } = tecnicos.find(t => t.tec.id === tec.id) || {};
                    const pct = closure ? getCompletionPct(closure.assigned, closure.completed) : null;
                    return (
                      <td key={date.toDateString()}
                        onClick={() => closure && setSelectedClosure(closure)}
                        className={`p-1 text-center border border-gray-200 ${closure ? 'cursor-pointer' : ''} ${isToday ? 'bg-red-50' : isFuture ? 'bg-gray-50 opacity-30' : 'bg-white'}`}>
                        {closure ? (
                          <div className={`w-6 h-6 mx-auto rounded-full flex items-center justify-center font-bold text-xs ${
                            pct >= 100 ? 'bg-green-500 text-white' :
                            pct >= 75  ? 'bg-green-200 text-green-800' :
                            pct > 0    ? 'bg-amber-400 text-white' :
                                         'bg-red-400 text-white'
                          }`}>
                            {pct >= 100 ? '✓' : pct}
                          </div>
                        ) : (
                          <div className="w-6 h-6 mx-auto" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" />100% (completa)</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-200 inline-block" />75–99%</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400 inline-block" />1–74%</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-400 inline-block" />0%</span>
        </div>
      </Collapsible>

      {/* ── ANOMALÍAS ─────────────────────────────────────────────────────── */}
      <Collapsible
        title="Anomalías detectadas"
        isOpen={anomaliasOpen}
        onToggle={() => setAnomaliasOpen(v => !v)}
        badge={
          <span className={`px-2 py-0.5 rounded-full text-sm font-bold ${anomalias.length > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
            {anomalias.length}
          </span>
        }
      >
        {anomalias.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">Sin anomalías detectadas</p>
        ) : (
          <div className="space-y-2">
            {anomalias.map(({ closure, issues }) => (
              <div key={closure.id}
                onClick={() => setSelectedClosure(closure)}
                className="border border-red-200 bg-red-50 rounded-xl p-3 cursor-pointer hover:bg-red-100 transition-colors"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="font-semibold text-gray-800 text-sm">
                    {closure.employeeName || getTecnicoNombre(closure.employeeId) || closure.employeeId}
                    <span className="text-gray-400 font-normal text-xs ml-2">{formatDateShort(closure.date)}</span>
                  </span>
                  <span className="text-xs text-red-600 font-semibold flex-shrink-0">Ver →</span>
                </div>
                {issues.map((issue, i) => (
                  <div key={i} className="text-xs text-red-700">• {issue}</div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Collapsible>

      {/* ── DISPUTAS · DIR. DE OPERACIONES ───────────────────────────────── */}
      <Collapsible
        title="Disputas"
        isOpen={notasOpen}
        onToggle={() => setNotasOpen(v => !v)}
        badge={disputasAbiertas.length > 0 && (
          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-sm font-bold">{disputasAbiertas.length}</span>
        )}
      >
        {disputasAbiertas.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">No hay disputas abiertas</p>
        ) : (
          <div className="space-y-3">
            {disputasAbiertas.map(closure => (
              <div key={closure.id} className="border border-orange-200 bg-orange-50 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <span className="font-semibold text-sm text-gray-800">
                      {closure.employeeName || getTecnicoNombre(closure.employeeId) || closure.employeeId}
                    </span>
                    <span className="text-xs text-gray-400 ml-2">{formatDateShort(closure.date)}</span>
                  </div>
                  <button
                    onClick={() => setSelectedClosure(closure)}
                    className="text-xs text-orange-700 underline shrink-0"
                  >
                    Ver detalle →
                  </button>
                </div>

                {/* Nota del director */}
                <p className="text-sm text-gray-700 mb-2 line-clamp-2">{closure.disputeNote}</p>

                {/* Estado respuesta */}
                {closure.disputeReply ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mb-3">
                    <p className="text-xs font-semibold text-blue-700 mb-1">
                      {closure.employeeName || `Técnico ${closure.employeeId}`} respondió:
                    </p>
                    <p className="text-xs text-gray-700 line-clamp-2">{closure.disputeReply}</p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic mb-3">Esperando respuesta del técnico...</p>
                )}

                {/* Botón cerrar */}
                <div className="flex justify-end">
                  <button
                    onClick={async () => {
                      try { await closeDispute(closure.id); }
                      catch (e) { alert('Error al cerrar: ' + e.message); }
                    }}
                    className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-colors"
                  >
                    ✓ Cerrar disputa
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Collapsible>

      {/* ── HISTORIAL ─────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-lg border p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-bold text-gray-800">Historial de cierres</h3>
          <button
            onClick={() => exportCSV(filteredClosures, getTecnicoNombre)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-semibold"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Exportar CSV
          </button>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap gap-2 mb-4">
          <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white">
            <option value="all">Todo el tiempo</option>
            <option value="today">Hoy</option>
            <option value="week">Última semana</option>
            <option value="month">Último mes</option>
            <option value="quarter">Último trimestre</option>
            <option value="year">Último año</option>
          </select>
          <select value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white">
            <option value="all">Todos los técnicos</option>
            {tecnicos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
          <button
            onClick={() => setSoloIncompletas(v => !v)}
            className={`px-3 py-2 text-sm rounded-lg font-medium border transition-colors ${
              soloIncompletas ? 'bg-amber-100 border-amber-400 text-amber-800' : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
            }`}
          >
            Solo incompletas
          </button>
        </div>

        {/* Resumen */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <div className="bg-gray-50 border rounded-xl p-3">
            <div className="text-xs font-semibold text-gray-500 mb-1">Cierres</div>
            <div className="text-2xl font-bold text-gray-800">{stats.totalClosures}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-3">
            <div className="text-xs font-semibold text-green-700 mb-1">Completitud</div>
            <div className="text-2xl font-bold text-green-800">{stats.completionRate}%</div>
            <div className="text-xs text-green-600">{stats.totalCompleted}/{stats.totalAssigned}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
            <div className="text-xs font-semibold text-emerald-700 mb-1">Efectivo total</div>
            <div className="text-2xl font-bold text-emerald-800">€{stats.totalEfectivo.toFixed(2)}</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-xl p-3">
            <div className="text-xs font-semibold text-red-700 mb-1">Mejor técnico</div>
            <div className="text-base font-bold text-red-800 truncate">{stats.perTecnico[0]?.name || '—'}</div>
            <div className="text-xs text-red-600">{stats.perTecnico[0]?.completionRate || 0}%</div>
          </div>
        </div>

        {filteredClosures.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <div className="text-4xl mb-3">📋</div>
            <p className="font-semibold">No hay cierres para los filtros seleccionados</p>
          </div>
        ) : (
          <>
            {/* Mobile: cards */}
            <div className="block md:hidden space-y-3">
              {filteredClosures.map(closure => {
                const pct = getCompletionPct(closure.assigned, closure.completed);
                const ef  = getEfectivo(closure);
                return (
                  <div key={closure.id} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold text-gray-800 text-sm">
                          {closure.employeeName || getTecnicoNombre(closure.employeeId) || closure.employeeId}
                        </div>
                        <div className="text-xs text-gray-500">{formatDateShort(closure.date)}</div>
                      </div>
                      <StatusCell closure={closure} />
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span>{closure.completed || 0}/{closure.assigned || 0} órdenes</span>
                      {ef !== null && <span className="text-green-700 font-semibold">€{ef.toFixed(2)}</span>}
                      {closure.notes && <span title={closure.notes}>📝</span>}
                    </div>
                    <button onClick={() => setSelectedClosure(closure)}
                      className="mt-3 w-full py-1.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700">
                      Ver detalle
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full table-auto text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left p-3 font-semibold text-gray-700">Fecha</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Técnico</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Asignadas</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Completadas</th>
                    <th className="text-center p-3 font-semibold text-gray-700">% Éxito</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Efectivo</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Notas</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClosures.map(closure => {
                    const pct = getCompletionPct(closure.assigned, closure.completed);
                    const ef  = getEfectivo(closure);
                    return (
                      <tr key={closure.id} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="p-3 text-gray-700">
                          <div>{formatDateShort(closure.date)}</div>
                          <div className="text-xs text-gray-400">{formatTime(closure.createdAt || closure.date)}</div>
                        </td>
                        <td className="p-3 font-medium text-gray-800">
                          {closure.employeeName || getTecnicoNombre(closure.employeeId) || closure.employeeId}
                        </td>
                        <td className="p-3 text-center text-gray-700">{closure.assigned || 0}</td>
                        <td className="p-3 text-center font-semibold text-gray-800">{closure.completed || 0}</td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                            pct >= 100 ? 'bg-green-100 text-green-800' :
                            pct >= 80  ? 'bg-amber-100 text-amber-800' :
                                         'bg-red-100 text-red-800'
                          }`}>
                            {pct}%
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          {ef !== null
                            ? <span className="font-semibold text-green-700">€{ef.toFixed(2)}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="p-3 text-center">
                          {closure.notes
                            ? <span title={closure.notes} className="cursor-help">📝</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="p-3 text-center">
                          <button onClick={() => setSelectedClosure(closure)}
                            className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-semibold">
                            Ver
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>


      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {selectedClosure && (
        <RouteDetailModal closure={selectedClosure} onClose={() => setSelectedClosure(null)} />
      )}
      <AddCommissionModal
        isOpen={commissionOpen}
        onClose={() => { setCommissionOpen(false); setCommissionInit(null); }}
        onSubmit={async (data) => { await createCommission(data); }}
        initialValues={commissionInit}
      />

    </div>
  );
};

export default RouteClosuresList;
